import { navigate } from '@reach/router'
import cx from 'classnames'
import React from 'react'
import { Field, Form } from 'react-final-form'
import Media from 'react-media'
import { CSSTransition } from 'react-transition-group'

import { Cross, Microphone, Search } from '@cinch/oxd-components/icons/src'
import LoadingSpinner from '@cinch/oxd-components/loading-spinner/src'
import SVG from '@cinch/oxd-components/svg/src'
import { isUserGetMediaSupported } from '@cinch/oxd-utils/browser-support/src'
import { isIOS } from '@cinch/oxd-utils/browser-support/src'
import GlobalContext from '@cinch/oxd-utils/context/src'
import AzureSpeechService from '@cinch/oxd-utils/speech-to-text/src'
import tokens from '@cinch/oxd-utils/tokens/src'

import SearchBarIconButton from './components/searchbar-icon-button'

import * as styles from './search.module.css'

export interface SearchProps {
  formName?: string
  isOpen: boolean
  searchTerms: string
  searchIsPending: boolean
  setSearchTerms: (terms: string) => any
  vehicleSearch: (terms: string) => any
  onClose: () => any
  onExit?: () => any
}

const transitionClassnames = {
  enter: styles.containerEnter,
  enterActive: styles.containerEnterActive,
  enterDone: styles.containerEnterDone,
  exit: styles.containerExit,
  exitActive: styles.containerExitActive,
}

const defaultI18n: Cinch.WebAppTranslations['search'] = {
  search: '',
  placeholder: '',
  placeholderSmall: '',
  speechPlaceholder: '',
  byVoice: '',
  showSearch: '',
  hideSearch: '',
}

export const SearchElem: React.FC<SearchProps & { i18n: Cinch.WebAppTranslations['search'] }> = ({
  i18n = defaultI18n,
  isOpen,
  searchTerms,
  searchIsPending,
  setSearchTerms,
  formName,
  onExit,
  onClose,
  vehicleSearch,
}) => {
  const [isSpeechRecognitionOpen, setSpeechRecognitionState] = React.useState(false)
  const [isCurrentlyReceivingInput, setIsCurrentlyReceivingInput] = React.useState(false)
  const searchRef = React.createRef<HTMLInputElement>()
  const speechService = React.useRef(new AzureSpeechService())

  const keyPressHandler = ({ key, keyCode }: KeyboardEvent) => {
    if ((key && key.toLowerCase() === 'escape') || keyCode === 27) {
      onClose()
    }
  }

  React.useEffect(() => {
    if (!searchRef || !searchRef.current) {
      return
    }

    const elem = searchRef.current
    if (isOpen) {
      elem.focus()
      elem.addEventListener('keydown', keyPressHandler)
    } else {
      elem.removeEventListener('keydown', keyPressHandler)
    }

    return () => {
      elem.removeEventListener('keydown', keyPressHandler)
    }
  }, [isOpen])

  function onFormSubmit(values: object) {
    const { search } = values as { [key: string]: any }
    const trimmedQuery = (search && search.trim()) || ''

    if (trimmedQuery.length === 0) {
      return
    }

    setSearchTerms(trimmedQuery)
    vehicleSearch(trimmedQuery)
    onClose()
    setTimeout(() => {
      setSearchTerms('')
      navigate(`/find-vehicle?query=${trimmedQuery}`, { state: { trimmedQuery }, replace: false })
    }, 500)
  }

  const startSpeechRecognition = (cb: any) => async () => {
    setSpeechRecognitionState(true)
    const { text } = await speechService.current.getTextFromSpeech({
      onSpeechStart: () => setIsCurrentlyReceivingInput(true),
      onSpeechEnd: () => setIsCurrentlyReceivingInput(false),
    })
    if (!text) {
      return setSpeechRecognitionState(false)
    }

    // remove all characters except alphanumeric, spaces and dashes
    const strippedText = text.replace(/[^a-z\d-\s]/gi, '')
    cb(strippedText)
    setSpeechRecognitionState(false)
    onFormSubmit({ search: strippedText })
  }

  return (
    <div className={cx(styles.search, { [styles.isOpen]: isOpen })}>
      <Form
        onSubmit={onFormSubmit}
        initialValues={{ search: searchTerms }}
        mutators={{
          updateSearchTerms: ([newValue], state) => state.fields.search.change(newValue),
        }}
        render={({ handleSubmit, invalid, form }) => (
          <form name={formName} className={styles.wrapper} aria-hidden={!isOpen} onSubmit={handleSubmit}>
            <CSSTransition
              timeout={250}
              classNames={transitionClassnames}
              in={isOpen}
              unmountOnExit={!isIOS()}
              onExited={onExit}
            >
              <div className={styles.container}>
                <SearchBarIconButton
                  className={styles.searchButton}
                  title={i18n.search}
                  type="submit"
                  disabled={invalid || searchIsPending}
                >
                  {({ inputSize }) =>
                    searchIsPending || isCurrentlyReceivingInput ? (
                      <LoadingSpinner size={inputSize} />
                    ) : (
                      <SVG children={Search} size={inputSize} />
                    )
                  }
                </SearchBarIconButton>
                <Field
                  name="search"
                  type="search"
                  render={({ input }) => (
                    <Media defaultMatches={true} query={tokens.customMedia.xs}>
                      {(matches) => (
                        <input
                          ref={searchRef}
                          placeholder={
                            isSpeechRecognitionOpen
                              ? i18n.speechPlaceholder
                              : matches
                              ? i18n.placeholder
                              : i18n.placeholderSmall
                          }
                          className={styles.input}
                          {...input}
                          data-site-search={true}
                          data-testid="search-field"
                        />
                      )}
                    </Media>
                  )}
                />
                {isUserGetMediaSupported() && (
                  <SearchBarIconButton
                    className={cx({
                      [styles.voiceButton]: isSpeechRecognitionOpen,
                      [styles.pulse]: isCurrentlyReceivingInput,
                    })}
                    title={i18n.byVoice}
                    disabled={isSpeechRecognitionOpen}
                    onClick={startSpeechRecognition(form.mutators.updateSearchTerms)}
                  >
                    {({ inputSize }) => <SVG children={Microphone} size={inputSize} />}
                  </SearchBarIconButton>
                )}
              </div>
            </CSSTransition>
          </form>
        )}
      />
      {isOpen && (
        <SearchBarIconButton
          onClick={onClose}
          aria-expanded={isOpen}
          title={i18n.hideSearch}
          className={styles.closeButton}
        >
          {({ toggleSize }) => <SVG children={Cross} size={toggleSize} />}
        </SearchBarIconButton>
      )}
    </div>
  )
}

const SearchComponent: React.FC<SearchProps> = (props) => {
  return (
    <GlobalContext.Consumer>
      {({ search }: Cinch.Context) => <SearchElem i18n={search} {...props} />}
    </GlobalContext.Consumer>
  )
}

export default SearchComponent
